import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import type { ApiResponse } from '@/types';

export interface ToolDefinitionDto {
  tool: string;
  label?: string;
  provider?: string;
  riskLevel: string;
  description: string;
  inputSchema: object;
  outputSchema: object;
  /** True for a tag / close / change-status tool — run after the reply, never during it. */
  postProcessing?: boolean;
  testable?: boolean;
  /** True when the platform runs the tool itself (e.g. followup_wait), not as an ordinary call. */
  platformHandled?: boolean;
  /** True for a read tool a Wait block may run when it fires (tracking / shipment status). */
  waitAction?: boolean;
}

export interface ToolDefinitionAction {
  id: string;
  label: string;
  provider: string;
  color: 'green' | 'blue';
  tool: string;
  description: string;
  inputSchema: object;
  outputSchema: object;
  riskLevel: string;
  /** True for a tag / close / change-status tool — offered only in its own picker, never mid-flow. */
  postProcessing?: boolean;
  /** True for a tool that can be run from its action's Test tab. */
  testable?: boolean;
  /** True when the platform runs the tool itself (e.g. followup_wait), not as an ordinary call. */
  platformHandled?: boolean;
  /** True for a read tool a Wait block may run when it fires (tracking / shipment status). */
  waitAction?: boolean;
}

interface ToolRunRequestDto {
  provider: string;
  tool: string;
  input: Record<string, unknown>;
}

/** One run's answer: the tool's data, or the tool server's error code and message. */
export interface ToolRunResult {
  status: 'success' | 'error';
  data?: unknown;
  code?: string;
  message?: string;
  latencyMs: number;
  requestId: string;
}

interface ToolDefinitionsListResponse {
  tools: ToolDefinitionDto[];
  count: number;
}

function deriveLabelFromTool(tool: string): string {
  return tool
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function deriveColor(provider: string): 'green' | 'blue' {
  return provider.toLowerCase() === 'shopify' ? 'green' : 'blue';
}

export const toolDefinitionService = {
  /**
   * The actions the workflow editor offers under `/`.
   *
   * Brand-scoped: the backend narrows the list to the providers the selected brand has
   * connected, so an operator working as one brand is never offered another brand's actions.
   * An unscoped call still returns the whole account, which is what it did before brands.
   */
  async getAll(scope: { bu_id?: string } = {}): Promise<ToolDefinitionAction[]> {
    const response = await api.get<ApiResponse<ToolDefinitionsListResponse>>(
      withBuScope(API_ENDPOINTS.TOOL_DEFINITIONS.BASE, scope),
    );
    const tools = response.data.data.tools;
    return tools.map((d) => {
      const provider = d.provider!;
      return {
        id: d.tool,
        label: d.label || deriveLabelFromTool(d.tool),
        provider,
        color: deriveColor(provider),
        tool: d.tool,
        description: d.description,
        inputSchema: d.inputSchema,
        outputSchema: d.outputSchema,
        riskLevel: d.riskLevel,
        ...(d.postProcessing && { postProcessing: true }),
        ...(d.testable && { testable: true }),
        ...(d.platformHandled && { platformHandled: true }),
        ...(d.waitAction && { waitAction: true }),
      };
    });
  },

  /** Runs one tool on the live integration, scoped to the selected brand like the list. */
  async run(request: ToolRunRequestDto, scope: { bu_id?: string } = {}): Promise<ToolRunResult> {
    const response = await api.post<ApiResponse<ToolRunResult>>(
      withBuScope(API_ENDPOINTS.TOOL_DEFINITIONS.RUN, scope),
      request,
    );
    return response.data.data;
  },
};
